import { useEffect, useState } from 'react';
import { assetService } from '../services/AssetService';
import { useRBAC } from '../contexts/RBACContext';

/**
 * MAY THIS PERSON CHANGE ANYTHING IN THIS SITE? — one question, one answer, the server's.
 *
 * WHY A HOOK AND NOT A ROLE CHECK
 * Write authority is defined once on the server, by SITE_WRITE_PREDICATE_SQL, and returned
 * per site as `permission_level` on GET /api/assets. Screens that asked a DIFFERENT question
 * disagreed with the gate in both directions:
 *
 *   `isAdmin || hasRole('data_creator', siteId)` hid the asset table's edit mode, the
 *   Systems tree and the extract buttons from site admins, who the server admits; while
 *
 *   pages that asked NOTHING — IoT devices, integrations, diagram authoring, site plans, the
 *   viewer's Browser pane and its element panel — showed every write control to every
 *   member, so a read-only user clicked buttons that answered 403.
 *
 * Reading `permission_level` fixes both, and keeps fixing them: when the predicate changes
 * (migration 068 merged account_admin into site_admin; 069 gave onboarding assignees write
 * on the site they are onboarding), every screen follows without being edited.
 *
 * CACHED PER SITE, because several components ask at once — the viewer, its Browser pane and
 * its element panel are all on screen together — and the answer is a property of the site,
 * not of the asker.
 */
type SiteAccess = { write: boolean; upload: boolean };
const inFlight = new Map<string, Promise<SiteAccess>>();

/** Forget cached answers. Call after anything that can change a person's access. */
export function clearSiteWriteAccessCache(siteId?: string): void {
  if (siteId) inFlight.delete(siteId);
  else inFlight.clear();
}

function resolveAccess(siteId: string): Promise<SiteAccess> {
  let pending = inFlight.get(siteId);
  if (!pending) {
    pending = assetService
      .getAsset(siteId)
      .then((asset) => {
        const write = asset?.permission_level === 'write';
        // Upload is write minus an assignment with "Upload files" switched off (server:
        // SITE_UPLOAD_PREDICATE_SQL). An older server without the field: write decides.
        return { write, upload: write && (asset as any)?.can_upload_files !== false };
      })
      // A failed lookup must not hand out write. Read-only is the safe answer, and the
      // server refuses anyway — this only decides whether a button is shown.
      .catch(() => ({ write: false, upload: false }));
    inFlight.set(siteId, pending);
  }
  return pending;
}

const resolveWrite = (siteId: string) => resolveAccess(siteId).then((access) => access.write);

export function useSiteWriteAccess(siteId?: string | null): boolean {
  const { isAdmin } = useRBAC();
  const [canWrite, setCanWrite] = useState(false);

  useEffect(() => {
    if (isAdmin) { setCanWrite(true); return; }
    if (!siteId) { setCanWrite(false); return; }

    let cancelled = false;
    resolveWrite(siteId).then((allowed) => { if (!cancelled) setCanWrite(allowed); });
    return () => { cancelled = true; };
  }, [siteId, isAdmin]);

  return canWrite;
}

/**
 * MAY THIS PERSON UPLOAD FILES TO THIS SITE? Write access, unless every assignment that gives it
 * has "Upload files" switched off (Idan 2026-10-11: "able to create assets, but not able to upload
 * files to sites"). Gates the buttons that bring a file onto a site; the server refuses anyway.
 */
export function useSiteUploadAccess(siteId?: string | null): boolean {
  const { isAdmin } = useRBAC();
  const [canUpload, setCanUpload] = useState(false);

  useEffect(() => {
    if (isAdmin) { setCanUpload(true); return; }
    if (!siteId) { setCanUpload(false); return; }

    let cancelled = false;
    resolveAccess(siteId).then((access) => { if (!cancelled) setCanUpload(access.upload); });
    return () => { cancelled = true; };
  }, [siteId, isAdmin]);

  return canUpload;
}

export default useSiteWriteAccess;
